import { useEffect, useLayoutEffect, useRef } from 'react';

import { anelloDOro, bannerTurno, coriandoli, lampo, puntiInVolo, scintille, scrittaCentrale } from '@/lib/tavolo/effetti';
import { conSegno } from '@/lib/qcp/resoconto';
import { eraRilasciataColDito } from '@/components/chinesepoker/trascinaCp';
import type { Resoconto } from '@/lib/qcp/resoconto';

/**
 * LE ANIMAZIONI DEL TAVOLO del Chinese Poker (fase 2; scheda `2026-10-17-tavolo-premium-ofc.md`, «Animazioni»): il volo della carta dalla mano alla fila, l'arrivo delle carte
 * del giro, la carta dell'avversario che compare, l'anello d'oro alla fila completata, il «+N» che sale dal punteggio, «Sei in Fantasyland» e la fascia «Tocca a te».
 * ⛔ Solo PRESENTAZIONE: nascono dal confronto fra cio' che c'era e cio' che c'e (le carte con `data-carta`, il numero di carte per fila, il turno, il resoconto), mai dalle regole;
 * Web Animations API e trasformazioni (niente layout animato); con «meno movimento» non si muove niente; dove non c'e `animate` (jsdom) non succede niente e non si rompe niente.
 * Le primitive (scintille, anello, «+N», fascia, scritta, coriandoli, lampo) sono quelle del modulo comune (`lib/tavolo/effetti`, copie di #718).
 *
 * L'IDENTITA' di una carta e `data-carta="<r><s>"`: la stessa carta ha lo stesso nome in mano e sulla fila, quindi se una chiave c'era in un punto e adesso e in un altro la
 * carta si e MOSSA (volo, FLIP: dalla vecchia posizione alla nuova). ⚠️ Ma SOLO se ha cambiato CONTENITORE (mano → fila, fila → fila): uno spostamento dentro lo stesso contenitore (il riordino
 * di «Ordina», il nuovo passo del ventaglio quando una carta se ne va, l'ordine di una fila che il server rimette diversamente) NON e un volo — la mano ha gia la sua transizione, e un volo
 * sopra a quella e il «saltello» che Luca ha visto (I-807). Neanche la carta LASCIATA COL DITO su una fila vola dalla mano: ci e gia arrivata, col dito. Una chiave che compare dove non c'era e NUOVA: nella mano e un ARRIVO (scivola giu, a scaglioni), altrove
 * e la posata dell'avversario (compare con un piccolo lampo d'oro). Quando le nuove sono troppe in un colpo (una mano nuova, una riconnessione) non si anima niente: arrivano e basta.
 */

const MOLTE = 7;
const ORO = ['#ecd39a', '#d4af6a', '#f6e7bf', '#b8913f'];

const muoviPoco = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Il centro di un elemento. */
function centro(el: Element) {
  const b = el.getBoundingClientRect();
  return { x: b.left + b.width / 2, y: b.top + b.height / 2 };
}

/** Il tavolo vivo (in piedi o nel palco, anche dentro il tabellone che sta nel body): la radice di tutto cio' che questo file guarda. */
const tavolo = (): HTMLElement | null => document.querySelector<HTMLElement>('[data-tavolo-cp]');

/**
 * Il CONTENITORE di una carta: la mano, oppure una fila di una plancia (mia / dell'avversario / altra). Un volo e un cambio di contenitore; dentro lo stesso, una carta che si sposta
 * si sta solo riassestando.
 */
export function contenitoreDi(n: Element): string {
  if (n.closest('[data-mano-in-mano]')) return 'mano';
  const fila = n.closest('[data-fila]')?.getAttribute('data-fila') ?? '';
  const plancia = n.closest('[data-plancia-mia]') ? 'mia' : n.closest('[data-plancia-avv]') ? 'avv' : 'altro';
  return `${plancia}:${fila}`;
}

/**
 * Volo (FLIP), arrivo e comparsa delle carte nel tavolo. `chiave` cambia quando cambia il disegno (in piedi / palco): li' le posizioni non si confrontano.
 * Nel palco le coordinate dello schermo si riportano in unita di disegno: dividendo per la scala del palco (`data-palco-scala`) e, se il tabellone e girato di 90°
 * (`data-ruota="true"`), ruotandole di -90° (un asse x del palco e un asse y dello schermo).
 */
export function useVoloCarte(chiave: string): void {
  const prima = useRef<{ chiave: string; pos: Map<string, { x: number; y: number; inMano: boolean; dove: string }> } | null>(null);
  useLayoutEffect(() => {
    const el = tavolo();
    if (!el) return;
    const ora = new Map<string, { x: number; y: number; inMano: boolean; dove: string }>();
    const nodi = new Map<string, HTMLElement>();
    for (const n of Array.from(el.querySelectorAll<HTMLElement>('[data-carta]'))) {
      const k = n.getAttribute('data-carta')!;
      const b = n.getBoundingClientRect();
      ora.set(k, { x: b.left, y: b.top, inMano: n.closest('[data-mano-in-mano]') !== null, dove: contenitoreDi(n) });
      nodi.set(k, n);
    }
    const p = prima.current;
    prima.current = { chiave, pos: ora };
    if (!p || p.chiave !== chiave || muoviPoco()) return;
    if (![...nodi.values()].every((n) => typeof n.animate === 'function')) return;
    const scala = Number(el.closest('[data-palco-scala]')?.getAttribute('data-palco-scala') ?? 1) || 1;
    const ruotato = document.querySelector('[data-tabellone-orizzontale][data-ruota="true"]') !== null;
    const nuove = [...ora.keys()].filter((k) => !p.pos.has(k));
    if (nuove.length > MOLTE) return;
    for (const [k, nodo] of nodi) {
      const vecchia = p.pos.get(k);
      const adesso = ora.get(k)!;
      if (vecchia) {
        // stesso contenitore = riassetto, non volo; lasciata col dito = e gia arrivata
        if (vecchia.dove === adesso.dove || eraRilasciataColDito(k)) continue;
        const sx = (vecchia.x - adesso.x) / scala;
        const sy = (vecchia.y - adesso.y) / scala;
        if (Math.abs(sx) < 2 && Math.abs(sy) < 2) continue;
        const [dx, dy] = ruotato ? [sy, -sx] : [sx, sy];
        nodo.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 300, easing: 'cubic-bezier(.2,.8,.3,1)' });
      } else if (adesso.inMano) {
        const i = nuove.indexOf(k);
        nodo.animate([{ transform: 'translateY(-36px) scale(.85)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 360, delay: Math.max(0, i) * 70, easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'backwards' });
      } else {
        nodo.animate([{ transform: 'scale(.6)', opacity: 0 }, { transform: 'scale(1.08)', opacity: 1, offset: 0.6 }, { transform: 'none', opacity: 1 }], { duration: 320, easing: 'ease-out' });
        scintille(centro(nodo), 6, 26, ORO);
      }
    }
  });
}

export interface ParametriEffetti {
  /** quante carte ha ogni mia fila, e la loro capienza */
  mieFile: { front: number; middle: number; back: number } | null;
  /** tocca a me (solo con il tavolo pronto) */
  miaMossa: boolean;
  /** il resoconto della mano appena chiusa, se c'e */
  resoconto: Resoconto | null;
  /** il testo della fascia «Tocca a te» e quello della scritta «Sei in Fantasyland» */
  testi: { toccaATe: string; fantasyland: string };
  /** la vibrazione del CP (gia filtrata dalla preferenza) */
  vibra: { impact: (s?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void; notification: (t: 'error' | 'success' | 'warning') => void };
}

const CAPIENZA = { front: 3, middle: 5, back: 5 } as const;
const ETICHETTA = { front: 'TOP', middle: 'MID', back: 'BOT' } as const;

/** L'anello d'oro alla fila completata, la fascia «Tocca a te», il «+N» che sale e «Sei in Fantasyland». */
export function useEffettiCp({ mieFile, miaMossa, resoconto, testi, vibra }: ParametriEffetti): void {
  const fileRef = useRef(mieFile);
  const mossaRef = useRef<boolean | null>(null);
  const manoVistaRef = useRef<number | null>(null);

  // La fila COMPLETATA: da meno carte della capienza a tutta la capienza.
  useEffect(() => {
    const precedente = fileRef.current;
    fileRef.current = mieFile;
    if (!mieFile || !precedente || muoviPoco()) return;
    for (const f of ['front', 'middle', 'back'] as const) {
      if (precedente[f] < CAPIENZA[f] && mieFile[f] === CAPIENZA[f]) {
        const riga = tavolo()?.querySelector<HTMLElement>(`[data-plancia-mia] [data-fila="${ETICHETTA[f]}"]`);
        if (riga && typeof document.body.animate === 'function') {
          const b = riga.getBoundingClientRect();
          anelloDOro({ left: b.left, top: b.top, width: b.width, height: b.height }, 0, 2);
        }
        vibra.notification('success');
      }
    }
  }, [mieFile, vibra]);

  // La fascia «Tocca a te»: quando il turno PASSA a me (non al primo disegno).
  useEffect(() => {
    const prima = mossaRef.current;
    mossaRef.current = miaMossa;
    if (prima === false && miaMossa && !muoviPoco() && typeof document.body.animate === 'function') bannerTurno(testi.toccaATe);
  }, [miaMossa, testi.toccaATe]);

  // Il resoconto di una mano che non avevo ancora visto: il «+N» che sale dal mio punteggio, e se vado in Fantasyland la scritta, i coriandoli e il lampo.
  useEffect(() => {
    if (!resoconto || manoVistaRef.current === resoconto.mano) return;
    manoVistaRef.current = resoconto.mano;
    if (resoconto.fantasylandPerMe) vibra.notification('success');
    else if (resoconto.io?.foul) vibra.notification('warning');
    if (muoviPoco() || typeof document.body.animate !== 'function') return;
    const punteggio = tavolo()?.querySelector<HTMLElement>('[data-punteggio="io"]');
    if (punteggio) {
      const b = punteggio.getBoundingClientRect();
      puntiInVolo({ left: b.left, top: b.top, width: b.width, height: b.height }, conSegno(resoconto.netto), resoconto.netto >= 0 ? '#9be8c1' : '#ffb4bd', 0);
    }
    if (resoconto.fantasylandPerMe) {
      scrittaCentrale(testi.fantasyland, 120);
      coriandoli(40, 200);
      lampo(160, 0.3);
    }
  }, [resoconto, testi.fantasyland, vibra]);
}
